// =====================================================
// MOVE DETECTION — detects paragraphs that moved position
// between original and revised documents.
// =====================================================

import { tokenize, similarity, cheapSimilarity, computeWordDiff, normalizeText } from './diff-engine-core';
import type { DiffParagraph } from '../types';
import { TUNING } from './tuning';

const MOVE_THRESHOLD = TUNING.MOVE_THRESHOLD;
const MIN_MOVE_WORDS = TUNING.MIN_MOVE_WORDS;
// Above this similarity a move is treated as "clean" and rendered as a whole moved
// block; below it the block was edited in transit, so we attach the internal
// word-diff and render the inserts/deletes inside the move (Litera-style).
const MOVE_CLEAN_THRESHOLD = TUNING.MOVE_CLEAN_THRESHOLD;

export function detectMoves(result: DiffParagraph[]): void {
  const delCandidates: { idx: number; text: string; wordCount: number }[] = [];
  const insCandidates: { idx: number; text: string; wordCount: number }[] = [];

  for (let i = 0; i < result.length; i++) {
    const p = result[i];
    const wc = tokenize(p.text || '').length;
    if (p.type === 'para-delete' && wc >= MIN_MOVE_WORDS) {
      delCandidates.push({ idx: i, text: p.text!, wordCount: wc });
    }
    if (p.type === 'para-insert' && wc >= MIN_MOVE_WORDS) {
      insCandidates.push({ idx: i, text: p.text!, wordCount: wc });
    }
  }

  // Cross-compare all delete vs insert candidates
  const movePairs: { dc: typeof delCandidates[0]; ic: typeof insCandidates[0]; sim: number }[] = [];
  for (const dc of delCandidates) {
    for (const ic of insCandidates) {
      const lenRatio = dc.text.length / Math.max(1, ic.text.length);
      if (lenRatio > TUNING.MOVE_LENGTH_RATIO || lenRatio < 1 / TUNING.MOVE_LENGTH_RATIO) continue;
      // Quick set-overlap check before expensive LCS
      if (cheapSimilarity(dc.text, ic.text) < TUNING.MOVE_PREFILTER) continue;
      const sim = similarity(dc.text, ic.text);
      if (sim >= MOVE_THRESHOLD) {
        movePairs.push({ dc, ic, sim });
      }
    }
  }
  movePairs.sort((a, b) => b.sim - a.sim);

  const usedDel = new Set<number>();
  const usedIns = new Set<number>();
  let moveId = 0;

  for (const { dc, ic, sim } of movePairs) {
    if (usedDel.has(dc.idx) || usedIns.has(ic.idx)) continue;
    usedDel.add(dc.idx);
    usedIns.add(ic.idx);

    (result[dc.idx] as any).type = 'move-from';
    (result[dc.idx] as any).moveId = moveId;
    (result[ic.idx] as any).type = 'move-to';
    (result[ic.idx] as any).moveId = moveId;
    (result[ic.idx] as any).origRich = result[dc.idx].origRich;

    // Moved-and-edited: attach the internal word-diff so the destination shows
    // exactly what changed in transit (moved-but-unchanged tokens stay typed as
    // 'move-to'; genuine inserts/deletes render normally). A clean move (or one
    // whose diff is all-equal) keeps the simple whole-block rendering.
    if (sim < MOVE_CLEAN_THRESHOLD) {
      const moveDiff = computeWordDiff(result[dc.idx].text || '', result[ic.idx].text || '');
      if (moveDiff.some(d => d.type !== 'equal')) {
        for (const d of moveDiff) if (d.type === 'equal') (d as any).type = 'move-to';
        (result[ic.idx] as any).diff = moveDiff;
      }
    }

    moveId++;
  }

  moveId = absorbShortNeighboursIntoBlockMoves(result, moveId);
  moveId = pairUniqueShortParagraphMoves(result, moveId);

  detectSubParagraphMoves(result, moveId);
}

/**
 * A short paragraph (a numbered heading such as "6. Intellectual Property.") that
 * travelled WITH a moved block is below MIN_MOVE_WORDS, so the pairing above leaves it
 * as a delete + insert in the middle of two moves. Reclaim it from context: a leftover
 * para-delete adjacent to a move-from, whose exact text is a leftover para-insert on
 * the same side of that move's move-to, moved with the block. Repeats so a run of
 * short paragraphs (heading + one-line lead-in) is absorbed one by one.
 */
function absorbShortNeighboursIntoBlockMoves(result: DiffParagraph[], moveId: number): number {
  const key = (t: string | undefined) => normalizeText(t || '').toLowerCase();
  let changed = true;
  while (changed) {
    changed = false;
    const toIdx = new Map<number, number>();
    for (let i = 0; i < result.length; i++) if (result[i].type === 'move-to' && result[i].moveId != null) toIdx.set(result[i].moveId!, i);
    for (let i = 0; i < result.length && !changed; i++) {
      const d = result[i];
      if (d.type !== 'para-delete' || !(d.text || '').trim()) continue;
      for (const side of [-1, 1]) {
        const m = result[i + side];
        if (!m || m.type !== 'move-from' || m.moveId == null) continue;
        const ti = toIdx.get(m.moveId);
        if (ti == null) continue;
        const ins = result[ti - side];           // d is on the opposite side of m, so its insert is on the opposite side of the move-to
        if (!ins || ins.type !== 'para-insert' || key(ins.text) !== key(d.text)) continue;
        (d as any).type = 'move-from'; (d as any).moveId = moveId;
        (ins as any).type = 'move-to'; (ins as any).moveId = moveId; (ins as any).origRich = d.origRich;
        moveId++; changed = true;
        break;
      }
    }
  }
  return moveId;
}

/**
 * A short paragraph (1–3 words: "6. Intellectual Property.", "ARTICLE V") that moved on
 * its own, with no moved block beside it, is below MIN_MOVE_WORDS and so far rendered as
 * a delete + insert. Legal documents are full of identical short paragraphs ("(a)",
 * "Definitions.", blank lines), so similarity cannot decide these; only uniqueness can.
 * Pair a leftover para-delete with a leftover para-insert when the normalised text is
 * UNIQUE on both sides of the diff — exactly one short para-delete and exactly one short
 * para-insert carry it, and it does not also occur in any other paragraph (equal,
 * modified or already moved) — and the text is substantial: at least 2 words or 6+
 * characters, never blank. Anything else keeps the MIN_MOVE_WORDS rule, so a heading
 * that is deleted in one place while a twin is inserted in another stays delete+insert.
 * Text is compared exactly (case included): a case change is an edit, not a move.
 */
function pairUniqueShortParagraphMoves(result: DiffParagraph[], moveId: number): number {
  const key = (t: string | undefined) => normalizeText(t || '');
  const dels = new Map<string, number[]>();
  const inss = new Map<string, number[]>();
  const elsewhere = new Set<string>();
  for (let i = 0; i < result.length; i++) {
    const p = result[i];
    const short = (p.type === 'para-delete' || p.type === 'para-insert') && tokenize(p.text || '').length < MIN_MOVE_WORDS;
    if (short) {
      const k = key(p.text);
      if (!k) continue;
      const m = p.type === 'para-delete' ? dels : inss;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(i);
    } else {
      for (const t of [p.text, p.origRich?.text, p.revRich?.text]) { const k = key(t); if (k) elsewhere.add(k); }
    }
  }
  for (const [k, di] of dels) {
    const ii = inss.get(k);
    if (di.length !== 1 || !ii || ii.length !== 1 || elsewhere.has(k)) continue;
    const wc = tokenize(k).length;
    if (wc < TUNING.MOVE_SHORT_MIN_WORDS && k.length < TUNING.MOVE_SHORT_MIN_CHARS) continue;
    const d = result[di[0]] as any, ins = result[ii[0]] as any;
    d.type = 'move-from'; d.moveId = moveId;
    ins.type = 'move-to'; ins.moveId = moveId; ins.origRich = d.origRich;
    moveId++;
  }
  return moveId;
}

// Minimum words for a sub-paragraph (clause-level) move, and the similarity it must
// hit. Conservative on purpose: only clear clause relocations should reclassify, so a
// stray boilerplate phrase or an in-place edit never masquerades as a move.
const SUBMOVE_MIN_WORDS = TUNING.SUBMOVE_MIN_WORDS;
const SUBMOVE_THRESHOLD = TUNING.SUBMOVE_THRESHOLD;

interface MoveSeg {
  text: string;
  words: number;
  pi: number;            // paragraph index in result
  start: number;         // diff-entry start (modify segments only)
  end: number;           // diff-entry end (inclusive)
  whole: boolean;        // true => whole para-delete/para-insert
  runStart?: number;     // where the run of changed entries starts, before `start` skips its glued lead
}

/** Collect maximal runs of consecutive same-`kind` diff entries as one segment each. A run
 *  that opens with glued pieces (the changed punctuation of the word before it: `outcome[-.”-]`)
 *  starts after them: they belong to that word, not to a clause that may have moved. */
function collectRuns(diff: any[], kind: string, pi: number, out: MoveSeg[]): void {
  let i = 0;
  while (i < diff.length) {
    if (diff[i].type !== kind) { i++; continue; }
    let j = i;
    while (j < diff.length && diff[j].type === kind) j++;
    let s = i;
    while (s < j && diff[s].glued) s++;
    const text = diff.slice(s, j).map((d: any) => d.value).join(' ').trim();
    const words = tokenize(text).length;
    if (words >= SUBMOVE_MIN_WORDS) out.push({ text, words, pi, start: s, end: j - 1, whole: false, runStart: i });
    i = j;
  }
}

/**
 * Detect clause-level (sub-paragraph) moves: a deleted run of text whose near-twin
 * appears as an inserted run elsewhere. Reclassifies the matched delete entries to
 * 'move-from' and the insert entries to 'move-to' (whole para-delete/insert paragraphs
 * flip their type) so a relocated clause renders as a move instead of a delete+insert.
 */
function detectSubParagraphMoves(result: DiffParagraph[], startMoveId: number): void {
  const dels: MoveSeg[] = [];
  const inss: MoveSeg[] = [];

  for (let pi = 0; pi < result.length; pi++) {
    const p = result[pi] as any;
    if (p.type === 'para-delete') {
      const w = tokenize(p.text || '').length;
      if (w >= SUBMOVE_MIN_WORDS) dels.push({ text: p.text, words: w, pi, start: -1, end: -1, whole: true });
    } else if (p.type === 'para-insert') {
      const w = tokenize(p.text || '').length;
      if (w >= SUBMOVE_MIN_WORDS) inss.push({ text: p.text, words: w, pi, start: -1, end: -1, whole: true });
    } else if (p.type === 'modify' && p.diff) {
      collectRuns(p.diff, 'delete', pi, dels);
      collectRuns(p.diff, 'insert', pi, inss);
    }
  }
  if (!dels.length || !inss.length) return;

  const cands: { di: number; ii: number; sim: number }[] = [];
  for (let di = 0; di < dels.length; di++) {
    for (let ii = 0; ii < inss.length; ii++) {
      const a = dels[di], b = inss[ii];
      // A replacement (delete run immediately followed by its insert run in the same
      // paragraph) is an in-place edit, not a relocation — never treat it as a move.
      if (!a.whole && !b.whole && a.pi === b.pi && (b.runStart === a.end + 1 || a.runStart === b.end + 1)) continue;
      const lenRatio = a.text.length / Math.max(1, b.text.length);
      if (lenRatio > TUNING.SUBMOVE_LENGTH_RATIO || lenRatio < TUNING.SUBMOVE_LENGTH_RATIO_MIN) continue;
      if (cheapSimilarity(a.text, b.text) < TUNING.SUBMOVE_PREFILTER) continue;
      const sim = similarity(a.text, b.text);
      if (sim >= SUBMOVE_THRESHOLD) cands.push({ di, ii, sim });
    }
  }
  cands.sort((x, y) => y.sim - x.sim);

  const usedD = new Set<number>(), usedI = new Set<number>();
  let moveId = startMoveId;
  for (const { di, ii, sim } of cands) {
    if (usedD.has(di) || usedI.has(ii)) continue;
    if (sim < MOVE_CLEAN_THRESHOLD) {
      // Moved AND edited: the edits must stay visible — a changed word inside a
      // relocated clause can never dissolve into "moved" markup. Rebuild the segment
      // word by word from the in-transit diff; if that is not possible, leave it as
      // a delete + insert rather than hide the edit.
      if (!applyEditedSegMove(result, dels[di], inss[ii], moveId, dels, inss)) continue;
    } else {
      applySegMarkup(result[dels[di].pi] as any, dels[di], 'move-from', moveId);
      applySegMarkup(result[inss[ii].pi] as any, inss[ii], 'move-to', moveId);
    }
    usedD.add(di); usedI.add(ii);
    moveId++;
  }
}

/** A run of diff entries is refinable when it is whole words, one per entry, in order. */
function runIsWholeWords(diff: any[], seg: MoveSeg, idxKey: 'origWordIdx' | 'revWordIdx'): boolean {
  if (seg.end - seg.start + 1 !== seg.words) return false;
  for (let k = seg.start; k <= seg.end; k++) {
    const e = diff[k];
    if (e.glued || typeof e[idxKey] !== 'number') return false;
    if (k > seg.start && e[idxKey] !== diff[k - 1][idxKey] + 1) return false;
  }
  return true;
}

/**
 * Mark a moved-and-edited clause so the edits stay visible. Whole paragraphs behave like
 * whole-paragraph moves: the destination carries the in-transit diff. A run inside a
 * paragraph is rebuilt from the diff: unchanged words become move-from / move-to,
 * changed words stay delete / insert on their own side. Returns false when a run cannot
 * be rebuilt safely (sub-word pieces, non-consecutive indices).
 */
function applyEditedSegMove(result: DiffParagraph[], a: MoveSeg, b: MoveSeg, moveId: number, dels: MoveSeg[], inss: MoveSeg[]): boolean {
  // a rebuilt run may have more or fewer entries than the one it replaces: shift the
  // indices of every later segment collected on the same paragraph (b included)
  const splice = (para: any, seg: MoveSeg, rebuilt: any[]) => {
    const delta = rebuilt.length - (seg.end - seg.start + 1);
    para.diff.splice(seg.start, seg.end - seg.start + 1, ...rebuilt);
    if (delta) for (const s of [...dels, ...inss]) if (s !== seg && !s.whole && s.pi === seg.pi && s.start > seg.end) { s.start += delta; s.end += delta; if (s.runStart != null) s.runStart += delta; }
    seg.end += delta;
  };
  const pa = result[a.pi] as any, pb = result[b.pi] as any;
  if (!a.whole && !runIsWholeWords(pa.diff, a, 'origWordIdx')) return false;
  if (!b.whole && !runIsWholeWords(pb.diff, b, 'revWordIdx')) return false;
  const moveDiff = computeWordDiff(a.text, b.text);
  if (!moveDiff.some(d => d.type !== 'equal')) {           // nothing actually changed: a clean move after all
    applySegMarkup(pa, a, 'move-from', moveId); applySegMarkup(pb, b, 'move-to', moveId);
    return true;
  }
  // A whole paragraph that became an edited clause inside another paragraph has nowhere
  // to show its dropped words: at the source the paragraph is one moved block, and at
  // the destination a deleted word would be restored into the WRONG paragraph by
  // reject-all. With deletions it stays a plain delete + insert; insert-only edits move.
  if (a.whole && !b.whole && moveDiff.some(d => d.type === 'delete')) return false;

  // original side
  if (a.whole) { pa.type = 'move-from'; pa.moveId = moveId; }
  else {
    const base = pa.diff[a.start].origWordIdx;
    const rebuilt: any[] = [];
    for (const d of moveDiff) {
      if (d.type === 'insert') continue;
      const e: any = { type: d.type === 'equal' ? 'move-from' : 'delete', value: d.value };
      if (typeof d.origWordIdx === 'number') e.origWordIdx = base + d.origWordIdx;
      if (typeof d.origCharIdx === 'number') e.origCharIdx = d.origCharIdx;   // a sub-word piece's place in its word (formatting)
      if (d.glued) e.glued = true;
      if (e.type === 'move-from') e.moveId = moveId;
      rebuilt.push(e);
    }
    splice(pa, a, rebuilt);
  }
  // revised side
  if (b.whole) {
    pb.type = 'move-to'; pb.moveId = moveId; pb.origRich = pa.origRich || pb.origRich;
    const dest = moveDiff.map(d => ({ ...d }));
    for (const d of dest as any[]) { if (d.type === 'equal') { d.type = 'move-to'; d.moveId = moveId; } }
    // deletions already shown word by word at the source stay there
    pb.diff = a.whole ? dest : dest.filter((d: any) => d.type !== 'delete');
  } else {
    const base = pb.diff[b.start].revWordIdx;
    const rebuilt: any[] = [];
    for (const d of moveDiff) {
      if (d.type === 'delete') continue;                       // shown at the source (a.whole with deletions never gets here)
      const e: any = { type: d.type === 'equal' ? 'move-to' : d.type, value: d.value };
      if (typeof d.revWordIdx === 'number') e.revWordIdx = base + d.revWordIdx;
      if (typeof d.revCharIdx === 'number') e.revCharIdx = d.revCharIdx;
      if (d.glued) e.glued = true;
      if (e.type === 'move-to') e.moveId = moveId;
      rebuilt.push(e);
    }
    splice(pb, b, rebuilt);
  }
  return true;
}

/** Flip a whole paragraph, or a run of diff entries, to a move type. */
function applySegMarkup(para: any, seg: MoveSeg, moveType: 'move-from' | 'move-to', moveId: number): void {
  if (seg.whole) {
    para.type = moveType;
    para.moveId = moveId;
  } else if (para.diff) {
    for (let k = seg.start; k <= seg.end; k++) {
      para.diff[k].type = moveType;
      para.diff[k].moveId = moveId;
    }
  }
}
